import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";
import { Download, RefreshCw, AlertTriangle, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = { cancelled: "Annullato", frozen: "Congelato", refunded: "Rimborsato" };
const NF = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0, useGrouping: "always" });
const eur = (v) => `€ ${NF.format(Math.round(v || 0))}`;
const pct = (v) => (v === null || v === undefined ? "n.d." : `${v}%`);
const itDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

const toCsv = (rows, cols) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.map(c => esc(c.label)).join(";"), ...rows.map(r => cols.map(c => esc(c.get(r))).join(";"))].join("\n");
};
const download = (name, text) => {
  const url = URL.createObjectURL(new Blob(["\ufeff" + text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const ANOMALY_COLS = [
  { label: "Cliente", get: r => r.client_name }, { label: "Sales", get: r => r.sales_rep_name }, { label: "Stato", get: r => STATUS[r.status] },
  { label: "Data inizio", get: r => r.start_date }, { label: "Data termine attuale", get: r => r.status_change_date || "" },
  { label: "ARR", get: r => r.arr }, { label: "MRR", get: r => r.mrr }, { label: "Problema", get: r => r.issue }, { label: "ID contratto", get: r => r.contract_id },
];
const ROW_COLS = [
  { label: "Periodo", get: r => r.period + (r.partial ? " (in corso)" : "") }, { label: "Data fotografia base", get: r => r.snapshot_date },
  { label: "ARR servito", get: r => r.arr_served }, { label: "ARR perso", get: r => r.arr_lost }, { label: "di cui Annullato", get: r => r.cancelled },
  { label: "di cui Congelato", get: r => r.frozen }, { label: "di cui Rimborsato", get: r => r.refunded }, { label: "% ARR persa sui serviti", get: r => r.churn_rate ?? "" },
  { label: "Clienti serviti", get: r => r.clients_served }, { label: "Clienti persi", get: r => r.logo_churn }, { label: "% clienti persi sui serviti", get: r => r.logo_churn_rate ?? "" },
  { label: "Riattivazioni", get: r => r.reactivations }, { label: "Clienti persi (nomi)", get: r => r.lost_clients.join(", ") },
];

const AnomalyRow = ({ a, onSaved }) => {
  const [val, setVal] = useState(a.status_change_date || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/contracts/${a.contract_id}/effective-date`, { status_change_date: val });
      toast.success(`Data aggiornata: ${a.client_name}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };
  return (
    <tr className="border-t border-border/40" data-testid={`anomaly-row-${a.contract_id}`}>
      <td className="px-3 py-2 font-medium">{a.client_name}<div className="text-[11px] text-muted-foreground">{a.sales_rep_name}</div></td>
      <td className="px-3 py-2 text-xs">{STATUS[a.status]}</td>
      <td className="px-3 py-2 text-xs">{itDate(a.start_date)}</td>
      <td className="px-3 py-2 text-xs text-right">{eur(a.arr)}</td>
      <td className="px-3 py-2 text-xs text-amber-700">{a.issue}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <Input type="date" className="h-8 w-36 text-xs" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`anomaly-date-${a.contract_id}`} />
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={save} disabled={saving || !val || val === a.status_change_date} data-testid={`anomaly-save-${a.contract_id}`}><Save className="h-3.5 w-3.5" /></Button>
        </div>
      </td>
    </tr>
  );
};

export const ChurnReview = () => {
  const [g, setG] = useState("quarter");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await axios.get(`${API}/admin/churn-review`, { params: { granularity: g } })).data); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore caricamento"); }
    setLoading(false);
  }, [g]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-10 flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-8" data-testid="churn-review">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">Verifica Churn</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Sola lettura, sui dati di questo ambiente al {itDate(data.today)}. Serviti = attivi il giorno prima dell'inizio del periodo + acquisiti nel periodo (stato alla data, ogni cliente contato una volta).
            Tasso = persi nel periodo / serviti, inclusi gli acquisiti e persi nello stesso periodo. Congelato conta come perso; le riattivazioni non cancellano la perdita.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-input overflow-hidden text-sm">
            {[["quarter", "Trimestri"], ["month", "Mesi"]].map(([k, l]) => (
              <button key={k} onClick={() => setG(k)} className={`px-3 py-1.5 ${g === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`churn-review-${k}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="gap-1" onClick={load} disabled={loading} data-testid="churn-review-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Ricalcola</Button>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => download(`verifica-churn-${g}.csv`, toCsv(data.rows, ROW_COLS))} data-testid="churn-review-export"><Download className="h-3.5 w-3.5" /> CSV</Button>
        </div>
      </div>

      <div className="rounded-lg border border-border/60 overflow-x-auto">
        <table className="w-full text-sm" data-testid="churn-review-table">
          <thead>
            <tr className="bg-muted/40 text-[11px] uppercase text-muted-foreground">
              {["Periodo", "Base al", "ARR servito", "ARR perso", "Annull.", "Cong.", "Rimb.", "% ARR persa sui serviti", "Clienti serviti", "Clienti persi", "% clienti persi sui serviti", "Riattiv."].map(h => (
                <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.period} className={`border-t border-border/40 ${r.partial ? "bg-amber-50/60" : ""}`} data-testid={`churn-review-row-${r.period}`} title={r.lost_clients.length ? `Clienti persi: ${r.lost_clients.join(", ")}` : ""}>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{r.period}{r.partial && <span className="ml-1 text-[10px] text-amber-700 font-semibold">IN CORSO</span>}</td>
                <td className="px-3 py-2 text-xs">{itDate(r.snapshot_date)}</td>
                <td className="px-3 py-2 text-right">{eur(r.arr_served)}</td>
                <td className="px-3 py-2 text-right">{eur(r.arr_lost)}</td>
                <td className="px-3 py-2 text-right text-xs">{eur(r.cancelled)}</td>
                <td className="px-3 py-2 text-right text-xs">{eur(r.frozen)}</td>
                <td className="px-3 py-2 text-right text-xs">{eur(r.refunded)}</td>
                <td className="px-3 py-2 text-right font-semibold">{pct(r.churn_rate)}</td>
                <td className="px-3 py-2 text-right">{r.clients_served}</td>
                <td className="px-3 py-2 text-right">{r.logo_churn}</td>
                <td className="px-3 py-2 text-right font-semibold">{pct(r.logo_churn_rate)}</td>
                <td className="px-3 py-2 text-right text-muted-foreground">{r.reactivations}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-3" data-testid="reconciliation">
        <div>
          <h3 className="font-semibold">Test di quadratura ARR mensile</h3>
          <p className="text-xs text-muted-foreground">ARR attivo a inizio mese + nuovo ARR − ARR perso + ARR riattivato = ARR attivo a fine mese. Le righe con differenza diversa da zero sono evidenziate.</p>
        </div>
        {(() => {
          const bad = data.reconciliation.filter(r => Math.abs(r.reconciliation_diff) > 0.01);
          return <p className={`text-sm font-medium ${bad.length ? "text-red-600" : "text-emerald-700"}`} data-testid="reconciliation-status">{bad.length ? `${bad.length} mesi con differenze` : `Quadratura OK su tutti i ${data.reconciliation.length} mesi`}</p>;
        })()}
        <div className="rounded-lg border border-border/60 overflow-x-auto">
          <table className="w-full text-sm" data-testid="reconciliation-table">
            <thead>
              <tr className="bg-muted/40 text-[11px] uppercase text-muted-foreground">
                {["Mese", "ARR inizio", "+ Nuovo", "− Perso", "+ Riattivato", "= Atteso", "ARR fine", "Differenza"].map(h => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.reconciliation.map(r => {
                const diff = Math.abs(r.reconciliation_diff) > 0.01;
                return (
                  <tr key={r.month} className={`border-t border-border/40 ${diff ? "bg-red-50" : r.partial ? "bg-amber-50/60" : ""}`} data-testid={`reconciliation-row-${r.month}`}>
                    <td className="px-3 py-2 font-medium">{r.month}{r.partial && <span className="ml-1 text-[10px] text-amber-700 font-semibold">IN CORSO</span>}</td>
                    <td className="px-3 py-2 text-right">{eur(r.arr_start)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{eur(r.arr_added)}</td>
                    <td className="px-3 py-2 text-right text-red-600">{eur(r.arr_lost)}</td>
                    <td className="px-3 py-2 text-right text-sky-700">{eur(r.arr_reactivated)}</td>
                    <td className="px-3 py-2 text-right">{eur(r.arr_start + r.arr_added - r.arr_lost + r.arr_reactivated)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{eur(r.cumulative_arr)}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${diff ? "text-red-600" : "text-muted-foreground"}`}>{eur(r.reconciliation_diff)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3" data-testid="churn-anomalies">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Contratti persi con data termine da verificare ({data.anomalies.length})</h3>
            <p className="text-xs text-muted-foreground">Data termine mancante, uguale o precedente alla data di inizio. Correggi la data effettiva di termine qui o esporta la lista.</p>
          </div>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => download("contratti-data-termine-da-verificare.csv", toCsv(data.anomalies, ANOMALY_COLS))} disabled={!data.anomalies.length} data-testid="anomalies-export"><Download className="h-3.5 w-3.5" /> Esporta CSV</Button>
        </div>
        {data.anomalies.length > 0 ? (
          <div className="rounded-lg border border-border/60 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 text-[11px] uppercase text-muted-foreground">
                  {["Cliente", "Stato", "Data inizio", "ARR", "Problema", "Data effettiva termine"].map(h => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>{data.anomalies.map(a => <AnomalyRow key={a.contract_id} a={a} onSaved={load} />)}</tbody>
            </table>
          </div>
        ) : <p className="text-sm text-emerald-700" data-testid="anomalies-empty">Nessuna anomalia sulle date di termine.</p>}
      </div>
    </div>
  );
};
